import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  subscribeToSessionRefresh,
  SessionRefreshTrigger,
} from '../../services/sessionBroadcastService';

/**
 * Invisible global listener that watches for Master Admin "refresh all
 * sessions" broadcasts and forces a hard reload after a countdown so users
 * have time to save their work.
 *
 * Mounted once near the root of the authenticated app shell.
 */
export const SessionRefreshListener: React.FC = () => {
  const { currentUser } = useAuth();

  const [countdown, setCountdown] = useState<number | null>(null);
  const [triggeredByName, setTriggeredByName] = useState<string>('');

  // Anchor to "now" the moment this listener mounts. Any broadcast stamped
  // before this point is treated as already-handled (or pre-login) and
  // ignored, so freshly-logged-in users don't get nuked by yesterday's
  // refresh.
  const sessionStartRef = useRef<number>(Date.now());

  // Track the last triggeredAt we acted on so we don't double-fire on
  // unrelated metadata-only updates.
  const lastHandledAtRef = useRef<number>(0);

  // Triggers the user has explicitly dismissed (clicked "Exit & Save").
  // We refuse to ever re-show those even if the snapshot listener fires
  // again with the same broadcast (e.g. network reconnect, strict mode
  // re-subscribe, second tab, etc).
  const dismissedAtsRef = useRef<Set<number>>(new Set());

  // Pending setTimeout for the countdown tick. Stored as a ref so the
  // dismiss handler can cancel it synchronously instead of racing the
  // effect cleanup.
  const tickTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!currentUser) return;

    const unsub = subscribeToSessionRefresh((trigger: SessionRefreshTrigger) => {
      if (!trigger.triggeredAt) return;
      const triggeredAtMs = trigger.triggeredAt.toMillis();

      // Skip stale broadcasts (predate this session), duplicates, and
      // anything the user already dismissed.
      if (triggeredAtMs <= sessionStartRef.current) return;
      if (triggeredAtMs <= lastHandledAtRef.current) return;
      if (dismissedAtsRef.current.has(triggeredAtMs)) return;
      lastHandledAtRef.current = triggeredAtMs;

      // Don't force-refresh the admin who fired the broadcast — they were
      // intentional about it and may need to verify deploy went out before
      // their own browser reloads.
      if (trigger.triggeredBy && trigger.triggeredBy === currentUser.uid) {
        return;
      }

      const seconds = Math.max(5, Math.min(120, trigger.countdownSeconds ?? 30));
      setTriggeredByName(trigger.triggeredByName || 'Master Admin');
      setCountdown(seconds);
    });

    return unsub;
  }, [currentUser]);

  // Tick down once per second; reload when we hit zero.
  useEffect(() => {
    // Always clear any prior tick when state changes — guarantees a
    // dismiss synchronously kills the pending decrement.
    if (tickTimeoutRef.current !== null) {
      window.clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }

    if (countdown === null) return;
    if (countdown <= 0) {
      // Hard-reload bypassing the service worker cache so users land on
      // the latest deployed bundle.
      try {
        window.location.reload();
      } catch {
        window.location.href = window.location.href;
      }
      return;
    }
    tickTimeoutRef.current = window.setTimeout(() => {
      tickTimeoutRef.current = null;
      setCountdown((n) => (n === null ? null : n - 1));
    }, 1000);
    return () => {
      if (tickTimeoutRef.current !== null) {
        window.clearTimeout(tickTimeoutRef.current);
        tickTimeoutRef.current = null;
      }
    };
  }, [countdown]);

  if (countdown === null) return null;

  const refreshNow = () => {
    if (tickTimeoutRef.current !== null) {
      window.clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }
    try {
      window.location.reload();
    } catch {
      window.location.href = window.location.href;
    }
  };

  const dismissForNow = () => {
    // Hard-stop: cancel any pending tick, mark this broadcast as
    // dismissed so the snapshot listener won't re-show it, and clear
    // the modal.
    if (tickTimeoutRef.current !== null) {
      window.clearTimeout(tickTimeoutRef.current);
      tickTimeoutRef.current = null;
    }
    if (lastHandledAtRef.current) {
      dismissedAtsRef.current.add(lastHandledAtRef.current);
    }
    setCountdown(null);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-amber-50 border-b border-amber-100 px-6 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-500/15 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-gray-900">Session refresh incoming</h2>
            <p className="text-[11px] text-gray-600">Triggered by {triggeredByName}</p>
          </div>
        </div>

        <div className="px-6 py-5">
          <p className="text-[13px] text-gray-700 leading-relaxed">
            Your session will refresh automatically to load updates. <strong>Save your progress now</strong> —
            anything unsaved will be lost.
          </p>

          <div className="mt-5 flex items-center justify-center">
            <div className="relative w-28 h-28 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center">
              <RefreshCw className="absolute w-6 h-6 text-gray-300 animate-spin-slow" />
              <div className="text-center">
                <div className="text-[28px] font-bold text-gray-900 leading-none">{countdown}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">seconds</div>
              </div>
            </div>
          </div>

          <p className="mt-5 text-[11px] text-gray-500 text-center">
            The page will reload automatically when the timer ends.
          </p>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={dismissForNow}
              className="px-3 py-2.5 text-[12px] font-medium text-gray-700 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              Exit & Save Progress
            </button>
            <button
              type="button"
              onClick={refreshNow}
              className="px-3 py-2.5 text-[12px] font-medium bg-[#1A1A1A] text-white rounded-md hover:bg-[#2a2a2a] transition-colors"
            >
              Refresh Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionRefreshListener;
